import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Shield, Mail, Lock, User, Building2,
  Eye, EyeOff, ArrowRight, ArrowLeft, Loader2,
  AlertCircle, Heart, Sparkles, CheckCircle2
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";

const tabs = [
  { id: "student", label: "Volunteer Login", icon: GraduationCap },
  { id: "admin", label: "Admin Login", icon: Shield },
];

const VolunteerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, resetPassword, isAuthenticated, user } = useAuth();

  // "login" | "register" | "forgot"
  const [view, setView] = useState("login");
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "admin" ? "admin" : "student"
  );

  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Register fields
  const [regName, setRegName] = useState("");
  const [regCollege, setRegCollege] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);

  // Forgot password fields
  const [resetEmail, setResetEmail] = useState("");

  // Colleges from Supabase
  const [colleges, setColleges] = useState([]);
  const [collegesLoading, setCollegesLoading] = useState(true);
  const [collegesError, setCollegesError] = useState("");

  // Shared state
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // If already authenticated, redirect to appropriate dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(user.role === "admin" ? "/admin/dashboard" : "/volunteer/dashboard", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch colleges for registration dropdown
  useEffect(() => {
    const fetchColleges = async () => {
      setCollegesLoading(true);
      const { data, error } = await supabase
        .from("colleges")
        .select("id, name")
        .order("name");
      if (error) {
        setCollegesError("Unable to load colleges. Please try again later.");
      } else {
        setColleges(data || []);
      }
      setCollegesLoading(false);
    };
    fetchColleges();
  }, []);

  // Reset form when switching tabs
  useEffect(() => {
    setEmail("");
    setPassword("");
    setErrors({});
    setSubmitError("");
    setSubmitSuccess("");
    setShowPassword(false);
  }, [activeTab]);

  const resetRegForm = () => {
    setRegName("");
    setRegCollege("");
    setRegEmail("");
    setRegPassword("");
    setRegConfirm("");
    setShowRegPassword(false);
    setShowRegConfirm(false);
    setErrors({});
    setSubmitError("");
    setSubmitSuccess("");
  };

  const switchToRegister = () => {
    resetRegForm();
    setView("register");
  };

  const switchToLogin = () => {
    setErrors({});
    setSubmitError("");
    setSubmitSuccess("");
    setView("login");
  };

  const switchToForgot = () => {
    setResetEmail(email);
    setErrors({});
    setSubmitError("");
    setSubmitSuccess("");
    setView("forgot");
  };

  // ─── Forgot password handler (Supabase email reset) ───
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!resetEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail.trim())) {
      newErrors.resetEmail = "Please enter a valid email address";
    }
    setErrors(newErrors);
    setSubmitError("");
    setSubmitSuccess("");
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      await resetPassword(resetEmail.trim());
      setSubmitSuccess("Password reset email sent! Check your inbox.");
    } catch (err) {
      setSubmitError(err.message || "Unable to send reset email.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Login validation ───
  const validateLogin = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Register validation ───
  const validateRegister = () => {
    const newErrors = {};
    if (!regName.trim()) {
      newErrors.regName = "Volunteer name is required";
    } else if (regName.trim().length < 2) {
      newErrors.regName = "Name must be at least 2 characters";
    }
    if (!regCollege) {
      newErrors.regCollege = "Please select a college";
    }
    if (!regEmail.trim()) {
      newErrors.regEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) {
      newErrors.regEmail = "Please enter a valid email address";
    }
    if (!regPassword) {
      newErrors.regPassword = "Password is required";
    } else if (regPassword.length < 6) {
      newErrors.regPassword = "Password must be at least 6 characters";
    }
    if (!regConfirm) {
      newErrors.regConfirm = "Please confirm your password";
    } else if (regPassword !== regConfirm) {
      newErrors.regConfirm = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Login handler ───
  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateLogin()) return;

    setIsLoading(true);
    try {
      const userData = await login(email, password);

      if (activeTab === "admin" && userData.role !== "admin") {
        setSubmitError("This account is not an admin account. Please use the Volunteer Login tab.");
        setIsLoading(false);
        return;
      }
      if (activeTab === "student" && userData.role !== "student") {
        setSubmitError("This account is not a Volunteer account. Please use the Admin Login tab.");
        setIsLoading(false);
        return;
      }

      navigate(
        userData.role === "admin" ? "/admin/dashboard" : "/volunteer/dashboard",
        { replace: true }
      );
    } catch (err) {
      setSubmitError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Register handler ───
  const handleRegister = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!validateRegister()) return;

    setIsLoading(true);
    try {
      await register(regName.trim(), regEmail.trim(), regPassword, regCollege);
      setSubmitSuccess("Registration successful! You can now sign in with your credentials.");
      setTimeout(() => {
        setView("login");
        setEmail(regEmail);
        setPassword("");
        setSubmitSuccess("");
        setSubmitError("");
        setErrors({});
      }, 2000);
    } catch (err) {
      setSubmitError(err.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Shared input style helper ───
  const inputClass = (fieldKey) =>
    `w-full pl-11 pr-4 py-3 rounded-xl border text-sm font-body font-bold text-ku-purple outline-none transition-all duration-200 bg-ku-cream/30 focus:bg-white placeholder:text-gray-400 placeholder:font-normal ${errors[fieldKey]
      ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
      : "border-gray-200 focus:border-ku-purple focus:ring-2 focus:ring-ku-soft"
    } disabled:opacity-60`;

  const passwordInputClass = (fieldKey) =>
    `w-full pl-11 pr-11 py-3 rounded-xl border text-sm font-body font-bold text-ku-purple outline-none transition-all duration-200 bg-ku-cream/30 focus:bg-white placeholder:text-gray-400 placeholder:font-normal ${errors[fieldKey]
      ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
      : "border-gray-200 focus:border-ku-purple focus:ring-2 focus:ring-ku-soft"
    } disabled:opacity-60`;

  const selectClass = (fieldKey) =>
    `w-full pl-11 pr-4 py-3 rounded-xl border text-sm font-body font-bold text-ku-purple outline-none transition-all duration-200 bg-ku-cream/30 focus:bg-white appearance-none ${errors[fieldKey]
      ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
      : "border-gray-200 focus:border-ku-purple focus:ring-2 focus:ring-ku-soft"
    } disabled:opacity-60`;

  // ─── Error message component ───
  const FieldError = ({ field }) =>
    errors[field] ? (
      <motion.p
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xs font-bold text-red-500 flex items-center gap-1"
      >
        <AlertCircle className="w-3 h-3" />
        {errors[field]}
      </motion.p>
    ) : null;

  return (
    <div className="min-h-screen bg-ku-cream text-gray-800 overflow-x-hidden w-full antialiased selection:bg-ku-purple/20">

      {/* ─── Hero Section ─── */}
      <section className="relative w-full py-16 md:py-24 bg-gradient-to-br from-[#5D3294] via-[#462170] to-[#3B1E60] text-white overflow-hidden">
        <div className="absolute top-10 left-10 w-72 h-72 bg-ku-gold/10 rounded-full blur-3xl animate-float pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-float-delayed pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-ku-gold/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-5"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-widest text-white/90">
              <Sparkles className="w-4 h-4 text-ku-gold" />
              Volunteer Portal
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-[1.15] tracking-tight">
              {view === "login" ? (
                <>Welcome Back, <span className="text-ku-gold drop-shadow-sm">Changemaker</span></>
              ) : view === "register" ? (
                <>Join the <span className="text-ku-gold drop-shadow-sm">Kindness Movement</span></>
              ) : (
                <>Reset Your <span className="text-ku-gold drop-shadow-sm">Password</span></>
              )}
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto leading-relaxed font-medium">
              {view === "login"
                ? "Sign in to access your volunteer dashboard, track your impact, and continue spreading kindness."
                : view === "register"
                  ? "Create your volunteer account and start making an impact in communities worldwide."
                  : "We'll send you an email with a link to reset your password."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── Card Section ─── */}
      <section className="relative w-full -mt-12 pb-20">
        <div className="w-full mx-auto px-6 max-w-2xl">
          <AnimatePresence mode="wait">

            {/* ═══════════════════════════════════════ */}
            {/* ─── LOGIN VIEW ─── */}
            {/* ═══════════════════════════════════════ */}
            {view === "login" && (
              <motion.div
                key="login-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-3xl shadow-elevated border border-ku-soft/60 overflow-hidden"
              >
                {/* Tab Navigation */}
                <div className="flex border-b border-ku-soft/60">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      id={`tab-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative flex-1 flex items-center justify-center gap-2 px-4 py-4 font-accent text-sm font-bold transition-all duration-300 border-none cursor-pointer bg-transparent ${activeTab === tab.id
                          ? "text-ku-purple"
                          : "text-gray-400 hover:text-gray-600"
                        }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                      {activeTab === tab.id && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-ku-purple to-ku-violet rounded-full"
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}
                    </button>
                  ))}
                </div>

                {/* Form Area */}
                <div className="p-6 sm:p-8">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, x: activeTab === "student" ? -20 : 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: activeTab === "student" ? 20 : -20 }}
                      transition={{ duration: 0.25 }}
                    >
                      {/* Tab Header */}
                      <div className="mb-6">
                        <h2 className="text-xl font-black text-ku-purple tracking-tight">
                          {activeTab === "student" ? "Volunteer Login" : "Admin Login"}
                        </h2>
                        <p className="text-gray-500 text-sm mt-1 font-medium">
                          {activeTab === "student"
                            ? "Access your volunteer dashboard and track your journey."
                            : "Access the admin control panel."}
                        </p>
                      </div>

                      {/* Error Alert */}
                      <AnimatePresence>
                        {submitError && (
                          <motion.div
                            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                            animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                            className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium overflow-hidden"
                          >
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{submitError}</span>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Login Form */}
                      <form onSubmit={handleLogin} noValidate className="space-y-5">
                        {/* Email */}
                        <div className="space-y-1.5">
                          <label htmlFor={`${activeTab}-email`} className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                            Email Address
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                              id={`${activeTab}-email`}
                              type="email"
                              value={email}
                              onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                              placeholder="you@example.com"
                              autoComplete="email"
                              disabled={isLoading}
                              className={inputClass("email")}
                            />
                          </div>
                          <FieldError field="email" />
                        </div>

                        {/* Password */}
                        <div className="space-y-1.5">
                          <label htmlFor={`${activeTab}-password`} className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                            Password
                          </label>
                          <div className="relative">
                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                              id={`${activeTab}-password`}
                              type={showPassword ? "text" : "password"}
                              value={password}
                              onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })); }}
                              placeholder="Enter your password"
                              autoComplete="current-password"
                              disabled={isLoading}
                              className={passwordInputClass("password")}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-ku-purple transition-colors bg-transparent border-none cursor-pointer"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              tabIndex={-1}
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <FieldError field="password" />
                        </div>

                        {/* Forgot Password — Volunteer only */}
                        {activeTab === "student" && (
                          <div className="flex justify-end">
                            <button
                              type="button"
                              id="forgot-password-link"
                              className="text-xs font-bold text-ku-violet hover:text-ku-purple transition-colors bg-transparent border-none cursor-pointer underline underline-offset-2"
                              onClick={switchToForgot}
                            >
                              Forgot Password?
                            </button>
                          </div>
                        )}

                        {/* Submit */}
                        <button
                          type="submit"
                          id={`${activeTab}-login-btn`}
                          disabled={isLoading}
                          className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-full font-accent font-bold text-sm text-white transition-all duration-300 border-none cursor-pointer shadow-lg ${isLoading
                              ? "bg-gray-400 cursor-not-allowed shadow-none"
                              : "bg-gradient-to-r from-[#5B2D8E] via-[#7B4EAE] to-[#5B2D8E] hover:shadow-elevated hover:scale-[1.02] active:scale-[0.98]"
                            }`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Signing in...
                            </>
                          ) : (
                            <>
                              Sign In
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </form>

                      {/* Register Link — Volunteer only */}
                      {activeTab === "student" && (
                        <div className="mt-6 pt-5 border-t border-ku-soft/60 text-center">
                          <p className="text-sm text-gray-500 font-medium">
                            New Volunteer?{" "}
                            <button
                              type="button"
                              id="register-link"
                              className="font-bold text-ku-purple hover:text-ku-violet transition-colors bg-transparent border-none cursor-pointer underline underline-offset-2"
                              onClick={switchToRegister}
                            >
                              Register here
                            </button>
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            )}

            {/* ═══════════════════════════════════════ */}
            {/* ─── FORGOT PASSWORD VIEW ─── */}
            {/* ═══════════════════════════════════════ */}
            {view === "forgot" && (
              <motion.div
                key="forgot-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-3xl shadow-elevated border border-ku-soft/60 overflow-hidden"
              >
                <div className="p-6 sm:p-8">
                  <button
                    type="button"
                    onClick={switchToLogin}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-400 hover:text-ku-purple transition-colors bg-transparent border-none cursor-pointer mb-5"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </button>

                  <div className="mb-6">
                    <h2 className="text-xl font-black text-ku-purple tracking-tight">Reset Your Password</h2>
                    <p className="text-gray-500 text-sm mt-1 font-medium">
                      Enter your email and we'll send you a reset link.
                    </p>
                  </div>

                  <AnimatePresence>
                    {(submitError || submitSuccess) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                        animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                        className={`px-4 py-3 rounded-xl border text-sm font-medium ${submitError
                          ? "bg-red-50 border-red-200 text-red-700"
                          : "bg-emerald-50 border-emerald-200 text-emerald-700"
                          }`}
                      >
                        {submitError || submitSuccess}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={handleResetPassword} noValidate className="space-y-5">
                    <div className="space-y-1.5">
                      <label htmlFor="reset-email" className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                          id="reset-email"
                          type="email"
                          value={resetEmail}
                          onChange={(e) => { setResetEmail(e.target.value); setErrors((p) => ({ ...p, resetEmail: "" })); }}
                          placeholder="you@example.com"
                          autoComplete="email"
                          disabled={isLoading}
                          className={inputClass("resetEmail")}
                        />
                      </div>
                      <FieldError field="resetEmail" />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !!submitSuccess}
                      className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-full font-accent font-bold text-sm text-white transition-all duration-300 border-none cursor-pointer shadow-lg bg-gradient-to-r from-[#5B2D8E] via-[#7B4EAE] to-[#5B2D8E] hover:shadow-elevated hover:scale-[1.02] active:scale-[0.98] disabled:bg-gray-400 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Reset Link <Mail className="w-4 h-4" /></>}
                    </button>
                  </form>
                </div>
              </motion.div>
            )}

            {/* ═══════════════════════════════════════ */}
            {/* ─── REGISTER VIEW ─── */}
            {/* ═══════════════════════════════════════ */}
            {view === "register" && (
              <motion.div
                key="register-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-3xl shadow-elevated border border-ku-soft/60 overflow-hidden"
              >
                {/* Register Header */}
                <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-0">
                  <button
                    type="button"
                    onClick={switchToLogin}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-400 hover:text-ku-purple transition-colors bg-transparent border-none cursor-pointer mb-5"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </button>

                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-ku-purple to-ku-violet flex items-center justify-center shadow-sm">
                      <GraduationCap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-black text-ku-purple tracking-tight">Volunteer Registration</h2>
                      <p className="text-gray-500 text-sm font-medium">Create your volunteer account</p>
                    </div>
                  </div>
                </div>

                {/* Register Form */}
                <div className="p-6 sm:p-8">
                  {/* Error Alert */}
                  <AnimatePresence>
                    {submitError && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                        animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                        className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium overflow-hidden"
                      >
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{submitError}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Success Alert */}
                  <AnimatePresence>
                    {submitSuccess && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                        animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                        className="flex items-start gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium overflow-hidden"
                      >
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{submitSuccess}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={handleRegister} noValidate className="space-y-5">
                    {/* Row 1: Name + College */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Volunteer Name */}
                      <div className="space-y-1.5">
                        <label htmlFor="reg-name" className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                          Volunteer Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <input
                            id="reg-name"
                            type="text"
                            value={regName}
                            onChange={(e) => { setRegName(e.target.value); setErrors((p) => ({ ...p, regName: "" })); }}
                            placeholder="Your full name"
                            autoComplete="name"
                            disabled={isLoading}
                            className={inputClass("regName")}
                          />
                        </div>
                        <FieldError field="regName" />
                      </div>

                      {/* College Name — Dropdown from Supabase */}
                      <div className="space-y-1.5">
                        <label htmlFor="reg-college" className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                          College Name *
                        </label>
                        <div className="relative">
                          <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <select
                            id="reg-college"
                            value={regCollege}
                            onChange={(e) => { setRegCollege(e.target.value); setErrors((p) => ({ ...p, regCollege: "" })); }}
                            disabled={isLoading}
                            className={selectClass("regCollege")}
                          >
                            <option value="" disabled>
                              {collegesLoading ? "Loading colleges..." : "Select your college"}
                            </option>
                            {!collegesLoading && colleges.length === 0 && (
                              <option value="" disabled>
                                {collegesError || "No colleges available"}
                              </option>
                            )}
                            {colleges.map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <FieldError field="regCollege" />
                      </div>
                    </div>

                    {/* Email — full width */}
                    <div className="space-y-1.5">
                      <label htmlFor="reg-email" className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                          id="reg-email"
                          type="email"
                          value={regEmail}
                          onChange={(e) => { setRegEmail(e.target.value); setErrors((p) => ({ ...p, regEmail: "" })); }}
                          placeholder="you@example.com"
                          autoComplete="email"
                          disabled={isLoading}
                          className={inputClass("regEmail")}
                        />
                      </div>
                      <FieldError field="regEmail" />
                    </div>

                    {/* Row 2: Password + Confirm Password */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Password */}
                      <div className="space-y-1.5">
                        <label htmlFor="reg-password" className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                          Password *
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <input
                            id="reg-password"
                            type={showRegPassword ? "text" : "password"}
                            value={regPassword}
                            onChange={(e) => { setRegPassword(e.target.value); setErrors((p) => ({ ...p, regPassword: "" })); }}
                            placeholder="Min. 6 characters"
                            autoComplete="new-password"
                            disabled={isLoading}
                            className={passwordInputClass("regPassword")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegPassword(!showRegPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-ku-purple transition-colors bg-transparent border-none cursor-pointer"
                            aria-label={showRegPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                          >
                            {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <FieldError field="regPassword" />
                      </div>

                      {/* Confirm Password */}
                      <div className="space-y-1.5">
                        <label htmlFor="reg-confirm" className="block text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">
                          Confirm Password *
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <input
                            id="reg-confirm"
                            type={showRegConfirm ? "text" : "password"}
                            value={regConfirm}
                            onChange={(e) => { setRegConfirm(e.target.value); setErrors((p) => ({ ...p, regConfirm: "" })); }}
                            placeholder="Re-enter password"
                            autoComplete="new-password"
                            disabled={isLoading}
                            className={passwordInputClass("regConfirm")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegConfirm(!showRegConfirm)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-ku-purple transition-colors bg-transparent border-none cursor-pointer"
                            aria-label={showRegConfirm ? "Hide password" : "Show password"}
                            tabIndex={-1}
                          >
                            {showRegConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <FieldError field="regConfirm" />
                      </div>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      id="register-submit-btn"
                      disabled={isLoading || !!submitSuccess}
                      className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-full font-accent font-bold text-sm text-white transition-all duration-300 border-none cursor-pointer shadow-lg mt-2 ${isLoading || submitSuccess
                          ? "bg-gray-400 cursor-not-allowed shadow-none"
                          : "bg-gradient-to-r from-[#7B4EAE] via-[#9B6FCC] to-[#7B4EAE] hover:shadow-elevated hover:scale-[1.02] active:scale-[0.98]"
                        }`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating Account...
                        </>
                      ) : submitSuccess ? (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          Registered!
                        </>
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to login link */}
                  <div className="mt-6 pt-5 border-t border-ku-soft/60 text-center">
                    <p className="text-sm text-gray-500 font-medium">
                      Already have an account?{" "}
                      <button
                        type="button"
                        className="font-bold text-ku-purple hover:text-ku-violet transition-colors bg-transparent border-none cursor-pointer underline underline-offset-2"
                        onClick={switchToLogin}
                      >
                        Sign in
                      </button>
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Back to volunteering link */}
          <div className="mt-8 text-center">
            <Link
              to="/volunteering"
              className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-ku-purple transition-colors"
            >
              <Heart className="w-4 h-4" />
              Back to Volunteering Hub
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerLogin;
